/**
 * useConnection — drives the two-browser flow, in one-way or two-way mode, with
 * read receipts (ack/nak) that ride the return lane.
 *
 * Every payload is a small in-capsule JSON envelope: a message ('m') or a read
 * acknowledgment ('a'). When a message is received we render it and auto-send an
 * ack back on our send lane — so in a ONE-WAY send the otherwise-idle b2a lane
 * carries the signed "read" receipt (the ack/nak). Own messages move Sent ->
 * Read; if the peer link drops before an ack, they show Unconfirmed.
 *
 * Frame-0016 invariants still hold (src/session/** is lint-guarded against
 * persistence; visibilitychange/pagehide destroys the session). The container
 * is the single erasure point; TTL resets on any message OR ack.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  SessionContainer,
  parseCapsule,
  serializeCapsule,
  encodeEnvelope,
  decodeEnvelope,
  messageEnvelope,
  ackEnvelope,
  burnEnvelope,
  wipeBytes,
  type Envelope,
  type LaneId,
} from '@fume/session';
import { PeerLink, type LinkState } from '../webrtc/peerLink.js';
import { RendezvousClient, type RendezvousSession } from '../transport/rendezvous-client.js';
import {
  deriveSas,
  generateSeed,
  transcriptFor,
  encodeSeedForLink,
  decodeSeedFromLink,
  type SessionSeed,
} from './handshake.js';

export type Phase =
  | 'choose'
  | 'invite-created'
  | 'awaiting-invite'
  | 'answer-created'
  | 'connected'
  | 'destroyed';
export type Role = 'initiator' | 'responder';
export type Mode = 'oneway' | 'twoway';
export type Sender = 'me' | 'them';
export type MsgStatus = 'sent' | 'read' | 'unconfirmed';
export type DestructReason =
  | 'manual'
  | 'backgrounded'
  | 'pagehide'
  | 'idle-lapse'
  | 'peer-closed'
  | 'peer-burn';

export interface ChatMessage {
  readonly id: number;
  readonly from: Sender;
  readonly lane: LaneId;
  readonly seq: number;
  readonly text: string;
  status?: MsgStatus; // present on from === 'me'
  dissipateAt?: number; // ms wall-clock when this secret poofs (one-way mode only)
  dissipated?: boolean; // true once the plaintext has been dropped
}

export const TTL_SECONDS = 30; // default idle TTL when none is chosen / on the wire
const TTL_MIN = 3;
const TTL_MAX = 3600;
// 0.3 — grace window before a backgrounded (but not unloaded) session self-destructs.
// A quick app-switch (e.g. to paste the share link into a chat app and come back) must
// not nuke a live session; anything longer than this still wipes. Widens the frame-0016
// exposure window by at most this much — see docs/design/0.3-background-grace.md.
const BACKGROUND_GRACE_MS = 15_000;
const nowSeconds = (): number => Math.floor(Date.now() / 1000);

/** Coerce a ttl carried in invite meta (unknown, off the wire) to sane integer seconds. */
function readTtl(meta: Record<string, unknown> | undefined): number {
  const t = Number(meta?.['ttl']);
  return Number.isFinite(t) && t >= TTL_MIN && t <= TTL_MAX ? Math.floor(t) : TTL_SECONDS;
}

interface Refs {
  link: PeerLink | null;
  container: SessionContainer | null;
  seed: SessionSeed | null;
  role: Role | null;
  mode: Mode;
  rv: RendezvousSession | null;
  ttl: number;
}

export interface UseConnection {
  phase: Phase;
  role: Role | null;
  mode: Mode;
  amSender: boolean;
  linkState: LinkState | null;
  inviteCode: string;
  answerCode: string;
  shareLink: string;
  hosted: boolean;
  rendezvousAvailable: boolean;
  sas: string;
  messages: ChatMessage[];
  lastActivityAt: number;
  destructReason: DestructReason | null;
  error: string | null;
  ttlSeconds: number;
  startSession(mode: Mode, ttlSeconds: number): Promise<void>;
  startSessionHosted(mode: Mode, ttlSeconds: number): Promise<void>;
  joinViaLink(rendezvousId: string, seedCode: string): Promise<void>;
  beginJoin(): void;
  joinSession(inviteCode: string): Promise<void>;
  answerAccepted: boolean;
  submitAnswer(answerCode: string): Promise<void>;
  send(text: string): void;
  destruct(reason?: DestructReason): void;
  reset(): void;
}

export function useConnection(): UseConnection {
  const [phase, setPhase] = useState<Phase>('choose');
  const [role, setRole] = useState<Role | null>(null);
  const [mode, setMode] = useState<Mode>('twoway');
  const [linkState, setLinkState] = useState<LinkState | null>(null);
  const [inviteCode, setInviteCode] = useState('');
  const [answerCode, setAnswerCode] = useState('');
  const [shareLink, setShareLink] = useState('');
  const [hosted, setHosted] = useState(false);
  const [sas, setSas] = useState('');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [lastActivityAt, setLastActivityAt] = useState(0);
  const [destructReason, setDestructReason] = useState<DestructReason | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [answerAccepted, setAnswerAccepted] = useState(false);
  const [ttlSeconds, setTtlSeconds] = useState(TTL_SECONDS);

  const refs = useRef<Refs>({ link: null, container: null, seed: null, role: null, mode: 'twoway', rv: null, ttl: TTL_SECONDS });
  const nextId = useRef(0);
  const dissipateTimers = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map());

  const clearDissipateTimers = useCallback((): void => {
    dissipateTimers.current.forEach((t) => clearTimeout(t));
    dissipateTimers.current.clear();
  }, []);

  const markDissipated = useCallback((id: number): void => {
    dissipateTimers.current.delete(id);
    // Honest erase: drop the plaintext reference from state; keep only the status.
    setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, dissipated: true, text: '' } : m)));
  }, []);

  const scheduleDissipation = useCallback(
    (id: number, ms: number): void => {
      const t = setTimeout(() => markDissipated(id), ms);
      dissipateTimers.current.set(id, t);
    },
    [markDissipated],
  );

  const markStatus = useCallback((lane: LaneId, seq: number, status: MsgStatus): void => {
    setMessages((prev) =>
      prev.map((m) => (m.from === 'me' && m.lane === lane && m.seq === seq ? { ...m, status } : m)),
    );
  }, []);

  const destruct = useCallback((reason: DestructReason = 'manual', notifyPeer = true): void => {
    const r = refs.current;
    // Coordinated burn: tell the peer to wipe too, BEFORE we tear down.
    if (notifyPeer && r.link && r.container && !r.container.isDestroyed) {
      try {
        r.link.send(serializeCapsule(r.container.send(encodeEnvelope(burnEnvelope(reason)), nowSeconds())));
      } catch {
        /* channel already gone — peer wipes on its own idle/close */
      }
    }
    r.container?.destroy();
    r.link?.close();
    r.rv?.close();
    r.rv = null;
    clearDissipateTimers();
    setPhase((prev) => {
      if (prev === 'destroyed') return prev;
      setDestructReason(reason);
      setMessages([]); // wipe on-screen plaintext: drop references so the GC can reclaim them
      return 'destroyed';
    });
  }, [clearDissipateTimers]);

  // Seal + ship one envelope on our send lane; returns the capsule (or null).
  const shipEnvelope = useCallback((envelope: Envelope): { lane: LaneId; seq: number } | null => {
    const r = refs.current;
    if (!r.link || !r.container || r.container.isDestroyed) return null;
    const capsule = r.container.send(encodeEnvelope(envelope), nowSeconds());
    r.link.send(serializeCapsule(capsule));
    setLastActivityAt(Date.now());
    return { lane: capsule.lane, seq: capsule.seq };
  }, []);

  const onLinkOpen = useCallback((): void => {
    const r = refs.current;
    if (!r.link || !r.seed || !r.role || r.container) return;
    const seed = r.seed;
    const container = new SessionContainer({
      R: seed.R,
      dh: seed.dh,
      transcript: transcriptFor(seed),
      sessionId: seed.sessionId,
      role: r.role,
      idleTtlSeconds: r.ttl,
      createdAt: nowSeconds(),
      activeIdleWipe: true,
    });
    r.container = container;
    // Signaling is done the moment the P2P channel opens — burn the relay room.
    r.rv?.close();
    r.rv = null;

    r.link.onMessage((bytes) => {
      try {
        const capsule = parseCapsule(bytes);
        const plaintext = container.receive(capsule, nowSeconds());
        const env = decodeEnvelope(plaintext);
        wipeBytes(plaintext); // zero the decoded bytes (the derived string can't be wiped)
        setLastActivityAt(Date.now());
        if (env.t === 'm') {
          const id = nextId.current++;
          const dissipating = refs.current.mode === 'oneway';
          const dissipateAt = dissipating ? Date.now() + refs.current.ttl * 1000 : undefined;
          setMessages((prev) => [
            ...prev,
            { id, from: 'them', lane: capsule.lane, seq: capsule.seq, text: env.x, dissipateAt },
          ]);
          if (dissipating) scheduleDissipation(id, refs.current.ttl * 1000);
          shipEnvelope(ackEnvelope(capsule.lane, capsule.seq)); // read receipt (never ack an ack)
        } else if (env.t === 'a') {
          markStatus(env.l, env.s, 'read');
        } else if (env.t === 'burn') {
          destruct('peer-burn', false); // peer self-destructed → wipe our end, don't echo
        }
      } catch {
        destruct('idle-lapse');
      }
    });
    setLastActivityAt(Date.now());
    setPhase('connected');
  }, [destruct, shipEnvelope, markStatus, scheduleDissipation]);

  const attachLink = useCallback(
    (link: PeerLink): void => {
      link.onStateChange = (s) => {
        setLinkState(s);
        if ((s === 'closed' || s === 'failed') && refs.current.container && !refs.current.container.isDestroyed) {
          destruct('peer-closed', false);
        }
      };
      link.onOpen = onLinkOpen;
    },
    [onLinkOpen, destruct],
  );

  const startSession = useCallback(
    async (m: Mode, ttl: number): Promise<void> => {
      setError(null);
      try {
        const seed = generateSeed();
        const { link, inviteCode: code } = await PeerLink.createInvite(seed, { mode: m, ttl });
        refs.current = { link, container: null, seed, role: 'initiator', mode: m, rv: null, ttl };
        attachLink(link);
        setHosted(false);
        setRole('initiator');
        setMode(m);
        setTtlSeconds(ttl);
        setSas(deriveSas(seed));
        setInviteCode(code);
        setLinkState(link.state);
        setPhase('invite-created');
      } catch (e) {
        setError(`Could not start a session: ${(e as Error).message}`);
      }
    },
    [attachLink],
  );

  const startSessionHosted = useCallback(
    async (m: Mode, ttl: number): Promise<void> => {
      setError(null);
      try {
        const client = new RendezvousClient();
        if (!client.configured) {
          // No relay in this build — fall back to the copy/paste invite path.
          await startSession(m, ttl);
          return;
        }
        const seed = generateSeed();
        const { link, offerCode } = await PeerLink.createOffer({ mode: m, ttl });
        refs.current = { link, container: null, seed, role: 'initiator', mode: m, rv: null, ttl };
        attachLink(link);
        const session = await client.createSession();
        refs.current.rv = session;
        // Push the (seedless) offer as soon as the guest lands; take their answer.
        session.onPeerJoined(() => session.send(offerCode));
        session.onMessage((answer) => {
          void refs.current.link?.acceptAnswer(String(answer));
        });
        session.onClosed((reason) => {
          if (refs.current.container) return; // already connected — teardown expected
          if (reason === 'expired') setError('The connection link expired before your friend joined.');
        });
        const base = window.location.origin + window.location.pathname;
        setShareLink(`${base}#r=${session.rendezvousId}&s=${encodeSeedForLink(seed)}`);
        setHosted(true);
        setRole('initiator');
        setMode(m);
        setTtlSeconds(ttl);
        setSas(deriveSas(seed));
        setInviteCode('');
        setLinkState(link.state);
        setPhase('invite-created');
      } catch (e) {
        setError(`Could not start a hosted session: ${(e as Error).message}`);
      }
    },
    [attachLink, startSession],
  );

  const joinViaLink = useCallback(
    async (rendezvousId: string, seedCode: string): Promise<void> => {
      setError(null);
      try {
        const seed = decodeSeedFromLink(seedCode);
        const client = new RendezvousClient();
        const session = await client.joinSession(rendezvousId);
        refs.current = { link: null, container: null, seed, role: 'responder', mode: 'twoway', rv: session, ttl: TTL_SECONDS };
        setHosted(true);
        setRole('responder');
        setSas(deriveSas(seed));
        setPhase('awaiting-invite');
        session.onMessage((offer) => {
          if (refs.current.link) return; // offer already consumed
          void (async () => {
            try {
              const { link, answerCode: answer, meta } = await PeerLink.acceptOffer(String(offer));
              const m: Mode = meta?.mode === 'oneway' ? 'oneway' : 'twoway';
              const ttl = readTtl(meta);
              refs.current.link = link;
              refs.current.mode = m;
              refs.current.ttl = ttl;
              attachLink(link);
              setMode(m);
              setTtlSeconds(ttl);
              session.send(answer);
              setLinkState(link.state);
              setPhase('answer-created');
            } catch (e) {
              setError(`Could not complete the connection: ${(e as Error).message}`);
            }
          })();
        });
        session.onClosed((reason) => {
          if (refs.current.container) return;
          if (reason === 'expired') setError('That link has expired.');
        });
      } catch (e) {
        setError(`Could not open that link: ${(e as Error).message}`);
      }
    },
    [attachLink],
  );

  const beginJoin = useCallback((): void => {
    setError(null);
    setPhase('awaiting-invite');
  }, []);

  const joinSession = useCallback(
    async (code: string): Promise<void> => {
      setError(null);
      try {
        const { link, answerCode: answer, seed, meta } = await PeerLink.acceptInvite(code);
        const m: Mode = meta?.mode === 'oneway' ? 'oneway' : 'twoway';
        const ttl = readTtl(meta);
        refs.current = { link, container: null, seed, role: 'responder', mode: m, rv: null, ttl };
        attachLink(link);
        setHosted(false);
        setRole('responder');
        setMode(m);
        setTtlSeconds(ttl);
        setSas(deriveSas(seed));
        setAnswerCode(answer);
        setLinkState(link.state);
        setPhase('answer-created');
      } catch (e) {
        setError(`Could not read that invite code: ${(e as Error).message}`);
      }
    },
    [attachLink],
  );

  const submitAnswer = useCallback(async (code: string): Promise<void> => {
    setError(null);
    try {
      await refs.current.link?.acceptAnswer(code);
      setAnswerAccepted(true);
    } catch (e) {
      setError(`Could not read that answer code: ${(e as Error).message}`);
    }
  }, []);

  const send = useCallback((text: string): void => {
    const trimmed = text.trim();
    if (trimmed.length === 0) return;
    try {
      const sent = shipEnvelope(messageEnvelope(trimmed));
      if (sent) {
        const id = nextId.current++;
        const dissipating = refs.current.mode === 'oneway';
        const dissipateAt = dissipating ? Date.now() + refs.current.ttl * 1000 : undefined;
        setMessages((prev) => [
          ...prev,
          { id, from: 'me', lane: sent.lane, seq: sent.seq, text: trimmed, status: 'sent', dissipateAt },
        ]);
        if (dissipating) scheduleDissipation(id, refs.current.ttl * 1000);
      }
    } catch {
      destruct('idle-lapse');
    }
  }, [shipEnvelope, destruct, scheduleDissipation]);

  const reset = useCallback((): void => {
    const r = refs.current;
    r.container?.destroy();
    r.link?.close();
    clearDissipateTimers();
    refs.current = { link: null, container: null, seed: null, role: null, mode: 'twoway', rv: null, ttl: TTL_SECONDS };
    nextId.current = 0;
    setPhase('choose');
    setRole(null);
    setMode('twoway');
    setLinkState(null);
    setInviteCode('');
    setAnswerCode('');
    setShareLink('');
    setHosted(false);
    setSas('');
    setMessages([]);
    setLastActivityAt(0);
    setDestructReason(null);
    setError(null);
    setAnswerAccepted(false);
    setTtlSeconds(TTL_SECONDS);
  }, [clearDissipateTimers]);

  // 0.3 — background grace period. A live session used to hard-destruct the instant the
  // tab was hidden (frame-0016), which collides with the leave-the-app-to-share step on
  // mobile. Now: on hide (or a bfcache pagehide, which may be restored) we start a short
  // grace timer and wipe only if we don't come back in time; a real unload (pagehide,
  // not persisted) still wipes immediately since JS can't run later. A lazy guard on
  // return also wipes if we were away longer than the grace (background timers may be
  // frozen). Pre-connect is unaffected (no container yet).
  useEffect(() => {
    let graceTimer: ReturnType<typeof setTimeout> | null = null;
    let hiddenAt = 0;
    const clearGrace = (): void => {
      if (graceTimer) {
        clearTimeout(graceTimer);
        graceTimer = null;
      }
    };
    const onHidden = (): void => {
      const c = refs.current.container;
      if (!c || c.isDestroyed) return;
      hiddenAt = Date.now();
      if (!graceTimer) {
        graceTimer = setTimeout(() => {
          graceTimer = null;
          destruct('backgrounded');
        }, BACKGROUND_GRACE_MS);
      }
    };
    const onVisible = (): void => {
      const c = refs.current.container;
      // Lazy guard: if we were away longer than the grace (timers can freeze in the
      // background), wipe now instead of silently resuming.
      if (c && !c.isDestroyed && hiddenAt > 0 && Date.now() - hiddenAt > BACKGROUND_GRACE_MS) {
        clearGrace();
        hiddenAt = 0;
        destruct('backgrounded');
        return;
      }
      clearGrace();
      hiddenAt = 0;
    };
    const onVisibility = (): void => {
      if (document.visibilityState === 'hidden') onHidden();
      else onVisible();
    };
    const onPageHide = (e: PageTransitionEvent): void => {
      const c = refs.current.container;
      if (!c || c.isDestroyed) return;
      if (e.persisted) {
        onHidden(); // going into bfcache — may be restored; treat as background
        return;
      }
      clearGrace();
      destruct('pagehide'); // real unload — wipe now
    };
    const onPageShow = (): void => onVisible();
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', onPageHide);
    window.addEventListener('pageshow', onPageShow);
    return () => {
      clearGrace();
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', onPageHide);
      window.removeEventListener('pageshow', onPageShow);
    };
  }, [destruct]);

  // Idle-lapse destruct, decoupled from the visual TTL graph (which is hidden in
  // the one-way flow). Fires ttl seconds after the last activity; resets on any
  // message/ack (lastActivityAt changes).
  useEffect(() => {
    if (phase !== 'connected' || lastActivityAt === 0) return;
    // One-way: per-message dissipation + the container's own idle key-wipe govern
    // erasure; the session stays up so the delivered/not-delivered status remains.
    if (refs.current.mode !== 'twoway') return;
    const fireAt = lastActivityAt + refs.current.ttl * 1000;
    const t = setTimeout(() => destruct('idle-lapse'), Math.max(0, fireAt - Date.now()));
    return () => clearTimeout(t);
  }, [phase, lastActivityAt, destruct]);

  const amSender = mode === 'twoway' || role === 'initiator';

  return {
    phase, role, mode, amSender, linkState, inviteCode, answerCode, shareLink, hosted, sas, messages,
    lastActivityAt, destructReason, error, answerAccepted, ttlSeconds,
    rendezvousAvailable: new RendezvousClient().configured,
    startSession, startSessionHosted, joinViaLink, beginJoin, joinSession, submitAnswer, send, destruct, reset,
  };
}
